/**
 * FM capability upsell metadata — sync with /PRODUCT_CAPABILITY_AUDIT.json
 */
import {
  formatPlanTierLabel,
  planIdToRank,
  requiredTierRank,
  type CapabilityGateState,
  type PlanTierId,
} from "@structura/core/capability-gate";
import { getSmLimitsForPlan, type SmPlanLimits } from "@/lib/plan-limits";
import {
  PLAN_FLAG_TEMPLATES,
  TENANT_PRODUCT_FLAG_KEYS,
  TENANT_PRODUCT_FLAG_PLAN_TIER,
  planIdToFlagTemplate,
  type TenantProductFlagKey,
} from "@/lib/tenant-product-flags";

export type FmCapabilityCode =
  | "loyalty_points"
  | "gift_cards"
  | "variants_addons"
  | "customer_accounts"
  | "produccion_b2b";

const FEATURE_MIN_PLAN: Record<FmCapabilityCode, "pro" | "business"> = {
  loyalty_points: "pro",
  gift_cards: "pro",
  variants_addons: "pro",
  customer_accounts: "pro",
  produccion_b2b: "business",
};

const FM_CAPABILITY_CODES = new Set<string>([
  "loyalty_points",
  "gift_cards",
  "variants_addons",
  "customer_accounts",
  "produccion_b2b",
]);

export const FM_CAPABILITY_UPSELL: Record<FmCapabilityCode, import("@structura/core/capability-gate").CapabilityUpsellCopy & { minPlan: "pro" | "business" }> = {
  loyalty_points: {
    minPlan: "pro",
    headline: "Programa de puntos",
    benefits: [
      "Acumulá puntos en cada pedido entregado",
      "Fidelizá clientes recurrentes sin cupones manuales",
      "Reversión automática en devoluciones",
    ],
    ctaLabel: "Ver plan Pro",
  },
  gift_cards: {
    minPlan: "pro",
    headline: "Gift cards digitales",
    benefits: [
      "Vendé tarjetas regalo con saldo trazable",
      "Historial de movimientos por cliente",
      "Canje en checkout online",
    ],
    ctaLabel: "Ver plan Pro",
  },
  variants_addons: {
    minPlan: "pro",
    headline: "Variantes y extras",
    benefits: [
      "Tamaños, sabores y opciones en el catálogo",
      "Extras en checkout con precio dinámico",
      "Más ticket promedio por pedido",
    ],
    ctaLabel: "Ver plan Pro",
  },
  customer_accounts: {
    minPlan: "pro",
    headline: "Cuentas de clientes",
    benefits: [
      "Login B2C en tu tienda",
      "Historial de pedidos y direcciones guardadas",
      "Push opt-in para recompra",
    ],
    ctaLabel: "Ver plan Pro",
  },
  produccion_b2b: {
    minPlan: "business",
    headline: "B2B / Mayorista",
    benefits: [
      "Canales mayoristas para cualquier nicho (moda, mercado, gastro…)",
      "Portal institucional con precios y cuenta corriente",
      "Pedidos B2B con descuentos por canal y trazabilidad",
    ],
    ctaLabel: "Ver plan Business",
  },
};

function isProductFlagKey(f: string): f is TenantProductFlagKey {
  return (TENANT_PRODUCT_FLAG_KEYS as readonly string[]).includes(f);
}

function isBooleanLimitKey(feature: string, planId: string | null | undefined): boolean {
  const limits = getSmLimitsForPlan(planId);
  return feature in limits && typeof limits[feature as keyof SmPlanLimits] === "boolean";
}

function isKnownNavFeature(feature: string, planId: string | null | undefined): boolean {
  return isBooleanLimitKey(feature, planId) || isProductFlagKey(feature) || FM_CAPABILITY_CODES.has(feature);
}

/**
 * Soft-nav: ¿el plan / snapshot del tenant incluye la feature?
 * Orden: grant en productFlags → SmPlanLimits → plantilla de plan → tier mínimo.
 * Nunca usar nombres marketing ("Professional"); el label sale de formatPlanTierLabel.
 */
/**
 * Soft-nav sync alineado a facade (`normalizeCapabilityKey` + dual either).
 * No async/DB: usa snapshot cliente (planId + productFlags).
 */
function planIncludesFeature(
  planId: string | null | undefined,
  feature: string,
  productFlags?: Record<string, boolean> | null,
): boolean {
  // Lazy require para evitar ciclos en tests — import tipado arriba no disponible sync circular
  // Usamos resolución inline equivalente a normalizeCapabilityKey
  const ALIAS: Record<string, { plan?: string; flag?: string }> = {
    portal_b2b: { plan: "portal_b2b", flag: "produccion_b2b" },
    produccion_b2b: { plan: "portal_b2b", flag: "produccion_b2b" },
    storefront: { plan: "storefront", flag: "catalog_public" },
    catalog_public: { plan: "storefront", flag: "catalog_public" },
  };
  const alias = ALIAS[feature];
  const planKey = alias?.plan ?? feature;
  const flagKey = alias?.flag ?? feature;
  const candidates = Array.from(new Set([feature, planKey, flagKey]));

  for (const k of candidates) {
    if (productFlags?.[k] === true) return true;
  }

  const limits = getSmLimitsForPlan(planId);
  for (const k of candidates) {
    if (k in limits) {
      const v = limits[k as keyof SmPlanLimits];
      if (typeof v === "boolean" && v) return true;
    }
  }

  for (const k of candidates) {
    if (isProductFlagKey(k)) {
      const template = PLAN_FLAG_TEMPLATES[planIdToFlagTemplate(planId)];
      if (template[k]) return true;
      const min = TENANT_PRODUCT_FLAG_PLAN_TIER[k];
      if (min && (min as string) !== "all") {
        if (planIdToRank(planId) >= requiredTierRank(min as PlanTierId)) return true;
      }
    }
  }
  return false;
}

function minPlanLabelForFeatures(features: string[]): string {
  const code = features.find((f) => FM_CAPABILITY_CODES.has(f)) as FmCapabilityCode | undefined;
  if (code) return formatPlanTierLabel(FEATURE_MIN_PLAN[code]);
  for (const f of features) {
    if (isProductFlagKey(f)) {
      const min = TENANT_PRODUCT_FLAG_PLAN_TIER[f];
      if (min && (min as string) !== "all") return formatPlanTierLabel(min);
    }
  }
  return formatPlanTierLabel("pro");
}

/**
 * Soft gate de nav: ítem visible con lock solo si el plan NO incluye la feature.
 * Acepta SmPlanLimits boolean + product flags. Keys desconocidas → included
 * (evita pintar "PRO" en módulos core mal cableados, caso Frosty/Business).
 */
export function resolveNavPlanGate(
  planId: string | null | undefined,
  features?: Array<keyof SmPlanLimits | string>,
  productFlags?: Record<string, boolean> | null,
): { state: CapabilityGateState; code?: FmCapabilityCode; minPlanLabel?: string } {
  if (!features?.length) return { state: "included" };
  const keys = features.map(String);
  if (keys.some((f) => planIncludesFeature(planId, f, productFlags))) {
    return { state: "included" };
  }
  const known = keys.filter((f) => isKnownNavFeature(f, planId));
  if (!known.length) return { state: "included" };
  const code = known.find((f) => FM_CAPABILITY_CODES.has(f)) as FmCapabilityCode | undefined;
  return {
    state: "locked_feature",
    code,
    minPlanLabel: minPlanLabelForFeatures(known),
  };
}

export function fmLockedPlanMessage(minPlanLabel: string, headline: string): string {
  return `Disponible en ${minPlanLabel}. ${headline} no está incluido en tu plan actual.`;
}
